import { createSlice, createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { LoginResponse, AuthState, RegisterResponse } from './types';
import { login, register } from '../../services/authService';
import { setCookie, deleteCookie, getCookie } from '../../utils/cookies';


const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk<LoginResponse, { email: string; password: string }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      console.log('Login attempt with:', credentials);
      const response = await login(credentials);
      console.log('Login response:', response);
      
      if (response.token) {
        console.log('Setting token cookie...');
        setCookie('token', response.token);
        
        // Verify cookie was set
        const storedToken = getCookie('token');
        console.log('Stored token verification:', storedToken);
        
        if (!storedToken) {
          console.error('Failed to set token cookie!');
          return rejectWithValue('Failed to store authentication token');
        }
      }
      
      return response;
    } catch (error) {
      console.error('Login error:', error);
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const registerUser = createAsyncThunk<RegisterResponse, { email: string; password: string ,username:string}>(
  'auth/register',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await register(credentials);
      // Don't set user state on registration
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      // Any API cleanup if needed
      return Promise.resolve();
    } catch (error) {
      return rejectWithValue('Logout failed');
    }
  }
);

export const updateUserPhoto = createAction<string>('auth/updateUserPhoto');

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuth: (state) => {
      state.user = null;
      state.error = null;
      deleteCookie('token');
    },
    setUser: (state, action) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        // Don't modify user state on registration success
        state.loading = false;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserPhoto, (state, action) => {
        if (state.user) {
          state.user.profilePhoto = action.payload;
        }
      });
  },
});

export const { clearAuth, setUser } = authSlice.actions;

export default authSlice.reducer;